
import React, { useCallback, useContext, useEffect, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material//MoreHoriz';
import RefreshIcon from '@mui/icons-material//Refresh';
import { LinearProgress, MenuItem, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useGetPublicCompListBatchByDate } from 'services/hooks';
import { dbShortDate, formatDate } from 'utilities';
import { useRefreshHelpersWithContext } from './hooks';
import { GetPublicCompListBatchByDate, RefreshGPCOptionProps } from './RefreshGPCOption.props';
import { refreshGPCOptionClasses, RefreshGPCOptionRoot } from './RefreshGPCOption.styles';

const RefreshGPCOption = ({
  approach,
  approaches,
  setApproaches,
  closeApproachMenu,
  marketDate,
  marketCurrency,
}: RefreshGPCOptionProps) => {
  const gpcApproach = approach?.valuations_approach_gpc;
  const [isLoading, setIsLoading] = useState(false);

  const { financialsPeriods, valuation } = useContext(ValuationContext);

  const { getTickerListsByDate, getRefreshedApproaches } = useRefreshHelpersWithContext();
  const [publicCompsDataByDate, fetchGPCCompListsByDate]
    = useGetPublicCompListBatchByDate() as GetPublicCompListBatchByDate;

  useEffect(() => {
    if (!isEmpty(publicCompsDataByDate)) {
      const refreshedApproaches = getRefreshedApproaches({
        publicCompsDataByDate,
        outdatedApproaches: [approach ?? {}],
        approaches,
        marketDate: marketDate ? dbShortDate(marketDate) : undefined,
      });

      setIsLoading(false);
      closeApproachMenu();

      // This is for not lost the user introduced and not saved data when
      // the useEffect on src/pages/Valuations/Valuation.js try to syncronize
      // the valuation.valuations_approaches with the "approaches" state variable.
      // Actually we do the same when we add/remove an approach
      if (valuation) {
        valuation.valuations_approaches = [...refreshedApproaches];
      }

      // redraw approaches Accordion with the updated approach
      setApproaches([...refreshedApproaches]);
    }
  }, [
    publicCompsDataByDate,
    approaches,
    setApproaches,
    approach,
    closeApproachMenu,
    getRefreshedApproaches,
    valuation,
    marketDate,
  ]);

  const refreshGPCApproach = useCallback(async () => {
    if (isLoading || !financialsPeriods) {
      return;
    }
    setIsLoading(true);
    const tickerListsByDate = getTickerListsByDate(
      [approach],
      approaches,
      marketDate ? dbShortDate(marketDate) : undefined
    );
    fetchGPCCompListsByDate?.(tickerListsByDate, marketCurrency, financialsPeriods);
  }, [
    approach,
    approaches,
    isLoading,
    getTickerListsByDate,
    fetchGPCCompListsByDate,
    financialsPeriods,
    marketCurrency,
    marketDate,
  ]);

  return (
    <RefreshGPCOptionRoot>
      {isLoading ? (
        <LinearProgress color="secondary" />
      ) : (
        <LinearProgress color="secondary" variant="determinate" value={100} />
      )}
      <MenuItem onClick={refreshGPCApproach}>
        <div className={refreshGPCOptionClasses.menuItemRow}>
          <div>
            <Typography color="secondary" variant="h4">
              {isLoading ? 'REFRESHING' : 'REFRESH DATA'}
            </Typography>
            <Typography className={refreshGPCOptionClasses.refreshDateLabel} color="secondary" variant="caption">
              LAST REFRESH DATE: {formatDate({ date: gpcApproach?.last_refresh_date ?? new Date().toUTCString() })}
            </Typography>
          </div>
          <div className={refreshGPCOptionClasses.refreshIconContainer}>
            {isLoading ? (
              <MoreHorizIcon color="secondary" fontSize="small" />
            ) : (
              <RefreshIcon color="secondary" fontSize="small" />
            )}
          </div>
        </div>
      </MenuItem>
    </RefreshGPCOptionRoot>
  );
};

export default RefreshGPCOption;
